import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { TMarket } from '@/common/constants/markets';
import { ROUTES, TRoute } from '@/common/constants/routes';
import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { getDefaultLang } from '@/common/i18n';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { FeatureFlagService } from '@/common/integrations/feature-flag/feature-flag.service';
import {
  setGetEmployeeCompanyQueryData,
  setGetEmployeeQueryData,
} from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { EmployeeService } from '@/common/integrations/menhir/employee/employee/employee.service';
import { tokenToUser } from '@/common/integrations/menhir/employee/sessions/sessions.dto';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import {
  IUser,
  TSignInUserResponse,
} from '@/common/integrations/menhir/employee/sessions/sessions.types';
import { SecureStorageService } from '@/common/integrations/secure-storage/secure-storage.service';
import { StorageService } from '@/common/integrations/storage/storage.service';
import * as SplashScreen from 'expo-splash-screen';
import { AuthContext } from './AuthContext';

interface ReactProps {
  children: ReactNode;
}

const fetchInitialState = async () => {
  const token = await StorageService.getAuthToken();
  if (token) {
    try {
      return tokenToUser(StorageService.decodeAuthToken(token));
    } catch {
      await StorageService.clearTokens();
      return null;
    }
  }
  return null;
};

const getRedirectRoute = (initialRoute: string) => {
  const excludedRoutes = [
    ROUTES.RESET_PASSWORD,
    ROUTES.SIGNUP,
    ROUTES.SIGNIN,
    ROUTES.SSO_SIGNIN,
    ROUTES.ROOT,
  ] as TRoute[];
  const allRoutes = Object.values(ROUTES).filter(
    (route) => !excludedRoutes.includes(route)
  );
  const matchedRoute = allRoutes.find((route) => route === initialRoute);
  return matchedRoute ? initialRoute : '';
};

export const AuthProvider = ({ children }: ReactProps) => {
  const { i18n } = useTranslation();
  const [user, setUser] = useState<IUser | null>(null);
  const [ready, setReady] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false); // check if shows soft login page
  const redirectRouteRef = useRef(
    Platform.OS === 'web'
      ? getRedirectRoute(window.location.pathname)
      : undefined
  );

  useEffect(() => {
    const checkNeedsLogin = async () => {
      if (Platform.OS === 'web') return;
      const email =
        await SecureStorageService.getDataFromSecureStorage('email');
      setNeedsLogin(!!email);
    };
    const fetchUser = async () => {
      const user = await fetchInitialState();
      if (!user) {
        setReady(true);
      }
      if (user) await checkNeedsLogin();
      setUser(user);
    };
    fetchUser();
  }, []);

  const signIn = useCallback(
    async (authResponse: TSignInUserResponse) => {
      queryClient.clear();
      await StorageService.updateTokens(
        authResponse.token,
        authResponse.refresh_token,
        authResponse.user_agent_token
      );
      setReady(false);
      setUser(authResponse.user);
    },
    [setUser]
  );

  const signOut = useCallback(() => {
    redirectRouteRef.current = '';
    setUser(null);
  }, [setUser]);

  useEffect(() => {
    if (user) {
      (async () => {
        try {
          const employeeDetails = await EmployeeService.getEmployeeDetails(
            user.id
          );
          const companyDetails =
            await EmployeeService.getEmployeeCompanyDetails();
          setGetEmployeeQueryData(user.id, employeeDetails);
          setGetEmployeeCompanyQueryData(companyDetails);
          AnalyticsService.trackIdentification(employeeDetails, companyDetails);
          FeatureFlagService.setDefaultUser(employeeDetails, companyDetails);

          if (
            !['pending_review', 'accepted', 'left'].includes(
              employeeDetails.invitation ?? ''
            )
          ) {
            await AuthService.signOut();
          }
          if (employeeDetails.invitation === 'pending_review') {
            AnalyticsService.trackCustomEvent('onboarding_started');
          }
          const language = employeeDetails.language ?? getDefaultLang();
          i18n.changeLanguage(language);
          await StorageService.setLanguage(language);
          if (employeeDetails.shortName)
            await StorageService.setLoggedInUserShortName(
              employeeDetails.shortName
            );
          setReady(true);
        } catch (error) {
          await AuthService.signOut();
          signOut();
        }
      })();
    }
  }, [user, i18n, signOut]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const urlParams = new URLSearchParams(window.location.search);
      const marketParam = urlParams.get('market') as TMarket;
      if (marketParam) {
        AnalyticsService.setChatConversationFields(marketParam);
      }
    }
  }, []);

  useEffect(() => {
    if (ready) {
      SplashScreen.hideAsync();
    }
  }, [ready]);

  if (!ready) {
    return <LoadingPlaceholder />;
  }

  const contextValue = {
    user,
    signIn,
    signOut,
    initialRoute: redirectRouteRef.current,
    setNeedsLogin,
    needsLogin,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
